export type ChangelogCategory = 'novidade' | 'melhoria' | 'correcao' | 'seguranca';

export interface ChangelogEntry {
  id: string;
  category: ChangelogCategory;
  title: string;
  description: string;
  /** Optional screenshot / illustration — set manually */
  imageUrl?: string;
  /** Bullet points shown in the detail view */
  details?: string[];
}

export interface ChangelogRelease {
  version: string;
  date: string; // ISO date string
  entries: ChangelogEntry[];
}

export const changelog: ChangelogRelease[] = [
  {
    version: '1.0.21',
    date: '2026-07-23',
    entries: [
      {
        id: 'r21-1',
        category: 'novidade',
        title: 'Câmeras LPR Hikvision DS-TCG406',
        description: 'Suporte completo a câmeras de reconhecimento de placas Hikvision DS-TCG406. O sistema recebe eventos ANPR via webhook, autoriza o acesso e abre a cancela automaticamente pela ISAPI.',
        // imageUrl: '/images/changelog/hikvision-lpr.png',
        details: [
          'Suporte ao modelo DS-TCG406 com protocolo ANPR via ISAPI',
          'Recebimento de eventos via webhook multipart XML',
          'Autorização automática por placa, tipo de pessoa e horário de acesso',
          'Abertura da cancela via comando ISAPI com autenticação Digest',
          'Abertura manual disponível diretamente no painel operacional',
          'Configuração de câmeras na página Zonas e Dispositivos',
        ],
      },
      {
        id: 'r21-2',
        category: 'novidade',
        title: 'Normas Internas',
        description: 'Nova seção para cadastrar, visualizar e gerenciar as normas internas do condomínio. Disponível no painel operacional e na vista normal.',
        details: [
          'Cadastro de normas com título, descrição e categoria',
          'Visualização organizada por categoria no painel operacional',
          'Disponível na guarita e na vista normal do sistema',
          'Gerenciamento completo: criar, editar e excluir normas',
          'Controle de acesso por perfil de usuário',
        ],
      },
      {
        id: 'r21-3',
        category: 'novidade',
        title: 'Ocorrências',
        description: 'Módulo de registro e acompanhamento de ocorrências do condomínio. Registre, categorize e acompanhe o status de cada ocorrência.',
        details: [
          'Registro de ocorrências com categoria, prioridade e descrição detalhada',
          'Acompanhamento de status: Aberta, Em andamento e Resolvida',
          'Sistema de comentários para atualizações e histórico da ocorrência',
          'Disponível no painel operacional e na vista normal',
          'Notificações automáticas ao atualizar o status',
        ],
      },
      {
        id: 'r21-4',
        category: 'novidade',
        title: 'Preview ao vivo de câmeras MJPEG',
        description: 'Visualização ao vivo de câmeras MJPEG diretamente na interface, com gerenciamento inteligente de streams para evitar conexões duplicadas.',
        details: [
          'Suporte a streams MJPEG diretamente no navegador',
          'Registro global de streams ativos para evitar conexões duplicadas',
          'Preview disponível em múltiplos pontos do sistema',
          'Carregamento sob demanda — stream iniciado apenas quando visível',
        ],
      },
      {
        id: 'r21-5',
        category: 'melhoria',
        title: 'Serial number em câmeras Alphadigi',
        description: 'Campo de número de série adicionado nas câmeras Alphadigi para melhor identificação e controle de equipamentos.',
        details: [
          'Campo "Número de série" adicionado no cadastro de câmeras Alphadigi',
          'Sincronização automática do serial via heartbeat da câmera',
          'Facilita identificação física de equipamentos em campo',
          'Exibido na listagem e nos detalhes de cada câmera',
        ],
      },
    ],
  },
  {
    version: '1.0.20',
    date: '2026-07-10',
    entries: [
      {
        id: 'r20-1',
        category: 'novidade',
        title: 'Filtro de localização em Eventos Gerais',
        description: 'Novo filtro por localização na tela de Eventos Gerais, facilitando a busca por eventos de zonas e dispositivos específicos.',
        details: [
          'Filtro por zona, bloco ou unidade nos Eventos Gerais',
          'Combina com os filtros existentes de data, tipo e pessoa',
          'Resultado instantâneo sem recarregar a página',
        ],
      },
      {
        id: 'r20-2',
        category: 'melhoria',
        title: 'Relatórios renomeados para Eventos Gerais',
        description: 'A seção "Relatórios" foi renomeada para "Eventos Gerais" para melhor refletir o propósito da página.',
        details: [
          'Nome mais claro e intuitivo para a seção de eventos',
          'Mesmo caminho de navegação (/eventos-gerais)',
          'Sem impacto em relatórios exportados ou configurações salvas',
        ],
      },
      {
        id: 'r20-3',
        category: 'melhoria',
        title: 'Requisitos de foto aprimorados',
        description: 'Melhorias nos critérios e validações de qualidade de foto para cadastro de pessoas, com orientações mais claras para o usuário.',
        details: [
          'Validação de enquadramento, iluminação e nitidez da foto',
          'Mensagens de erro descritivas ao rejeitar uma foto',
          'Guia visual de boas práticas para captura de fotos',
          'Compatível com upload, webcam e câmeras IP de cadastro',
        ],
      },
    ],
  },
  {
    version: '1.0.19',
    date: '2026-06-25',
    entries: [
      {
        id: 'r19-1',
        category: 'novidade',
        title: 'Sincronização em lote de veículos por RFID',
        description: 'Novo recurso para sincronizar todos os veículos de um cliente de uma só vez via RFID, agilizando o processo de cadastramento em lote.',
        details: [
          'Sincronização de todos os veículos com tag RFID de uma só vez',
          'Progresso exibido em tempo real no modal de sincronização',
          'Funciona com todos os dispositivos RFID cadastrados no cliente',
          'Relatório de sucesso/falha por dispositivo ao final',
        ],
      },
      {
        id: 'r19-2',
        category: 'novidade',
        title: 'Modal de progresso de sincronização',
        description: 'Modal com progresso em tempo real durante sincronizações longas com dispositivos, exibindo status por dispositivo e estimativa de conclusão.',
        details: [
          'Progresso percentual individual por dispositivo',
          'Status visual: aguardando, em andamento, concluído, erro',
          'Cancela sincronização a qualquer momento sem perder o que já foi feito',
        ],
      },
      {
        id: 'r19-3',
        category: 'novidade',
        title: 'Notificações push',
        description: 'Suporte a notificações push para o aplicativo mobile, com alertas de acesso, entregas e eventos importantes do condomínio.',
        details: [
          'Alertas de acesso em tempo real no aplicativo mobile (Android e iOS)',
          'Notificações de entregas recebidas na portaria',
          'Alertas configuráveis por tipo de evento e horário',
          'Configurações de preferência por morador no aplicativo',
        ],
      },
    ],
  },
  {
    version: '1.0.18',
    date: '2026-06-10',
    entries: [
      {
        id: 'r18-1',
        category: 'novidade',
        title: 'Suporte a câmeras Intelbras no cadastro',
        description: 'Integração com câmeras Intelbras na tela de câmeras de cadastro, permitindo captura de fotos diretamente do equipamento durante o registro de pessoas.',
        details: [
          'Suporte aos modelos compatíveis com protocolo ISAPI da Intelbras',
          'Captura de foto diretamente da câmera durante o cadastro',
          'Configuração da câmera Intelbras em Configurações do Cliente',
          'Mesma experiência das câmeras Hikvision já suportadas',
        ],
      },
    ],
  },
  {
    version: '1.0.17',
    date: '2026-05-28',
    entries: [
      {
        id: 'r17-1',
        category: 'correcao',
        title: 'Correções na integração Guarita',
        description: 'Corrigidos problemas na integração com módulo guarita: envio correto de bitmask de receptores, sincronização em múltiplos receptores e remoção de dispositivos por AND-NOT de bits.',
        details: [
          'Corrigido envio de bitmask de receptores ao registrar dispositivos',
          'Sincronização correta quando o mesmo dispositivo está em múltiplos receptores',
          'Remoção de dispositivo usa AND-NOT de bits para não apagar outros receptores',
          'Corrigo bug de abertura de porta: receptor e saída passados corretamente',
        ],
      },
      {
        id: 'r17-2',
        category: 'melhoria',
        title: 'Filtros de busca em Chaves e Tags',
        description: 'Adicionados filtros de busca nas páginas de Chaves e Tags, facilitando a localização de itens em listas extensas.',
        details: [
          'Campo de busca por código, nome ou unidade na página de Chaves',
          'Campo de busca por código ou pessoa na página de Tags',
          'Filtragem em tempo real sem recarregar a página',
        ],
      },
    ],
  },
  {
    version: '1.0.16',
    date: '2026-05-15',
    entries: [
      {
        id: 'r16-1',
        category: 'novidade',
        title: 'Flag de período personalizado para visitantes',
        description: 'Visitantes agora podem ter um período de acesso personalizado definido na criação, com controle de data de início e fim.',
        details: [
          'Define data de início e fim do acesso ao criar um visitante',
          'Acesso bloqueado automaticamente fora do período definido',
          'Compatível com todos os tipos de visitante',
        ],
      },
      {
        id: 'r16-2',
        category: 'novidade',
        title: 'Modo quiosque',
        description: 'Novo modo quiosque para terminais de autoatendimento, com interface simplificada para recepção de visitantes.',
        details: [
          'Interface simplificada para terminais de autoatendimento',
          'Pré-cadastro e verificação de visitantes pelo próprio visitante',
          'Modo tela cheia sem acesso ao restante do sistema',
          'Configuração por cliente nas configurações do sistema',
        ],
      },
      {
        id: 'r16-3',
        category: 'melhoria',
        title: 'Melhorias na guarita',
        description: 'Diversas melhorias na integração com o módulo guarita, incluindo exibição de estado do portão e controles manuais.',
        details: [
          'Exibição do estado do portão (aberto/fechado) em tempo real',
          'Controles manuais de abertura acessíveis no painel operacional',
          'Melhoria na latência de eventos do módulo guarita',
        ],
      },
    ],
  },
];
